import DOMPurify from 'dompurify';

/**
 * Conversion + sanitising helpers for rich text.
 *
 * Task descriptions and comments used to be stored as Editor.js JSON. They are now stored as HTML
 * produced by TipTap. Everything that reads stored content goes through `richTextToHtml` (which
 * understands both shapes plus bare plain text) and `sanitizeHtml` before it reaches the DOM.
 */

const ALLOWED_TAGS = [
  'p', 'br', 'h1', 'h2', 'h3', 'h4', 'strong', 'b', 'em', 'i', 'u', 's', 'code', 'pre',
  'blockquote', 'ul', 'ol', 'li', 'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'hr', 'span', 'div', 'mark', 'label', 'input',
];

const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height', 'class', 'colspan',
  'rowspan', 'data-type', 'data-id', 'data-label', 'data-checked', 'type', 'checked', 'disabled',
];

/** Same as the DOMPurify default minus `data:` — inline base64 images are never rendered. */
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/** Everything outside printable ASCII — whitespace and control characters used to hide a scheme. */
const NON_PRINTABLE = /[^!-~]/g;

let hookInstalled = false;

function installHook(): void {
  if (hookInstalled) {
    return;
  }
  hookInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
    // Task-list checkboxes are rendered by the read-only view too; nothing there may be toggled.
    if (node.tagName === 'INPUT') {
      node.setAttribute('disabled', 'disabled');
    }
    // DOMPurify keeps `data:` URIs on media tags (img/audio/video) whatever ALLOWED_URI_REGEXP
    // says, so inline base64 has to be dropped here instead.
    ['src', 'href'].forEach((attr) => {
      const value = node.getAttribute(attr);
      if (value && /^data:/i.test(value.replace(NON_PRINTABLE, ''))) {
        node.removeAttribute(attr);
      }
    });
  });
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const escapeAttr = escapeHtml;

function stripTags(value: unknown): string {
  return String(value ?? '').replace(/<[^>]*>/g, '');
}

/**
 * Editor.js stored inline markup (bold, links, inline code, mention pills) inside its `text`
 * fields, so those strings are HTML already and must not be escaped — the sanitiser is what makes
 * them safe. The only rewrite is the legacy mention pill, which becomes a TipTap mention node so
 * that `@First Last` keeps both its text and the user id it points at.
 */
function inline(value: unknown): string {
  return String(value ?? '').replace(
    /<b\b([^>]*)>([\s\S]*?)<\/b>/gi,
    (match: string, attrs: string, inner: string) => {
      if (!/mention-pill/i.test(attrs)) {
        return match;
      }
      const idMatch = /data-user-id\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(attrs);
      const id = idMatch ? idMatch[1] ?? idMatch[2] ?? '' : '';
      const label = stripTags(inner).replace(/^@/, '').trim();
      return `<span data-type="mention" data-id="${escapeAttr(id)}" data-label="${escapeAttr(label)}">@${escapeHtml(label)}</span>`;
    },
  );
}

type ListItem = string | { content?: string; text?: string; items?: ListItem[]; meta?: { checked?: boolean } };

function listItemsToHtml(items: unknown, tag: 'ul' | 'ol'): string {
  if (!Array.isArray(items)) {
    return '';
  }
  return (items as ListItem[])
    .map((item) => {
      if (typeof item === 'string') {
        return `<li>${inline(item)}</li>`;
      }
      const content = inline(item?.content ?? item?.text ?? '');
      const nested = Array.isArray(item?.items) && item.items.length
        ? `<${tag}>${listItemsToHtml(item.items, tag)}</${tag}>`
        : '';
      return `<li>${content}${nested}</li>`;
    })
    .join('');
}

function taskListToHtml(items: unknown): string {
  if (!Array.isArray(items) || !items.length) {
    return '';
  }
  const rows = (items as ListItem[])
    .map((item) => {
      const raw = typeof item === 'string' ? { text: item } : item ?? {};
      const text = inline(raw.text ?? raw.content ?? '');
      const checked = Boolean((raw as { checked?: boolean }).checked ?? raw.meta?.checked);
      const nested = Array.isArray(raw.items) && raw.items.length ? taskListToHtml(raw.items) : '';
      return `<li data-type="taskItem" data-checked="${checked}"><p>${text}</p>${nested}</li>`;
    })
    .join('');
  return `<ul data-type="taskList">${rows}</ul>`;
}

function tableToHtml(data: any): string {
  const content: unknown[] = Array.isArray(data?.content) ? data.content : [];
  if (!content.length) {
    return '';
  }
  const row = (cells: unknown, cell: 'td' | 'th') => {
    const list = Array.isArray(cells) ? cells : [];
    return `<tr>${list.map((c) => `<${cell}>${inline(c)}</${cell}>`).join('')}</tr>`;
  };
  const withHeadings = Boolean(data?.withHeadings);
  const head = withHeadings ? `<thead>${row(content[0], 'th')}</thead>` : '';
  const bodyRows = withHeadings ? content.slice(1) : content;
  const body = bodyRows.length ? `<tbody>${bodyRows.map((r) => row(r, 'td')).join('')}</tbody>` : '';
  return `<table>${head}${body}</table>`;
}

function imageToHtml(data: any): string {
  const url = data?.file?.url ?? data?.url ?? '';
  if (!url) {
    return '';
  }
  const caption = typeof data?.caption === 'string' ? data.caption : '';
  const img = `<img src="${escapeAttr(url)}" alt="${escapeAttr(stripTags(caption))}">`;
  return caption ? `${img}<p><em>${inline(caption)}</em></p>` : img;
}

function blockToHtml(block: any): string {
  const type = String(block?.type ?? '').toLowerCase();
  const data = block?.data ?? {};

  switch (type) {
    case 'header': {
      const level = Math.min(4, Math.max(1, Number(data.level) || 2));
      return `<h${level}>${inline(data.text)}</h${level}>`;
    }
    case 'paragraph':
      return `<p>${inline(data.text)}</p>`;
    case 'list':
    case 'orderedlist':
    case 'nestedlist': {
      const style = String(data.style ?? (type === 'orderedlist' ? 'ordered' : 'unordered')).toLowerCase();
      if (style === 'checklist') {
        return taskListToHtml(data.items);
      }
      const tag = style === 'ordered' ? 'ol' : 'ul';
      return `<${tag}>${listItemsToHtml(data.items, tag)}</${tag}>`;
    }
    case 'checklist':
      return taskListToHtml(data.items);
    case 'quote': {
      const caption = data.caption ? `<p><em>${inline(data.caption)}</em></p>` : '';
      return `<blockquote><p>${inline(data.text)}</p>${caption}</blockquote>`;
    }
    case 'code':
      return `<pre><code>${escapeHtml(data.code ?? data.text ?? '')}</code></pre>`;
    case 'delimiter':
      return '<hr>';
    case 'table':
      return tableToHtml(data);
    case 'image':
      return imageToHtml(data);
    case 'warning': {
      const message = data.message ? `<p>${inline(data.message)}</p>` : '';
      return `<blockquote><p><strong>${inline(data.title)}</strong></p>${message}</blockquote>`;
    }
    case 'embed': {
      const href = data.source ?? data.embed ?? '';
      if (!href) {
        return '';
      }
      const text = data.caption ? stripTags(data.caption) : String(href);
      return `<p><a href="${escapeAttr(href)}">${escapeHtml(text)}</a></p>`;
    }
    case 'linktool': {
      const href = data.link ?? '';
      if (!href) {
        return '';
      }
      const text = data.meta?.title ? stripTags(data.meta.title) : String(href);
      return `<p><a href="${escapeAttr(href)}">${escapeHtml(text)}</a></p>`;
    }
    case 'raw':
      return String(data.html ?? '');
    default:
      return typeof data.text === 'string' ? `<p>${inline(data.text)}</p>` : '';
  }
}

/** True when the string is an Editor.js document (`{ "blocks": [...] }`). */
export function isEditorJsJson(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  const trimmed = String(value).trim();
  if (!trimmed.startsWith('{')) {
    return false;
  }
  try {
    const parsed = JSON.parse(trimmed);
    return Boolean(parsed) && typeof parsed === 'object' && Array.isArray(parsed.blocks);
  } catch {
    return false;
  }
}

/** Converts a stored Editor.js document into the HTML TipTap understands. */
export function editorJsToHtml(json: string): string {
  let parsed: any;
  try {
    parsed = JSON.parse(json);
  } catch {
    return '';
  }
  const blocks = Array.isArray(parsed) ? parsed : parsed?.blocks;
  if (!Array.isArray(blocks)) {
    return '';
  }
  return blocks.map(blockToHtml).join('');
}

function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function plainTextToHtml(value: string): string {
  return value
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join('');
}

/** Normalises anything we may have stored (Editor.js JSON, HTML, plain text) into HTML. */
export function richTextToHtml(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return '';
  }
  if (isEditorJsJson(trimmed)) {
    return editorJsToHtml(trimmed);
  }
  if (looksLikeHtml(trimmed)) {
    return trimmed;
  }
  return plainTextToHtml(trimmed);
}

/** DOMPurify with the editor profile: no scripts, no `data:` URIs, every link opens safely. */
export function sanitizeHtml(html: string): string {
  if (!html) {
    return '';
  }
  installHook();
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP });
}

const BLOCK_END = /<\/(p|div|h[1-6]|li|tr|td|th|blockquote|pre|ul|ol|table)>/gi;
const LINE_BREAK = /<(br|hr)\s*\/?>/gi;

/** Plain-text preview of stored content, optionally cut at a word boundary with an ellipsis. */
export function richTextToPlain(value: string | null | undefined, limit?: number): string {
  const html = sanitizeHtml(richTextToHtml(value));
  if (!html) {
    return '';
  }
  const spaced = html.replace(LINE_BREAK, ' ').replace(BLOCK_END, ' ');
  const parsed = new DOMParser().parseFromString(spaced, 'text/html');
  const text = (parsed.body.textContent ?? '').replace(/\s+/g, ' ').trim();
  if (!limit || limit <= 0 || text.length <= limit) {
    return text;
  }
  let cut = text.slice(0, limit);
  if (!/\s/.test(text.charAt(limit))) {
    const boundary = cut.lastIndexOf(' ');
    if (boundary > 0) {
      cut = cut.slice(0, boundary);
    }
  }
  return `${cut.replace(/\s+$/, '')}…`;
}

/**
 * True when stored content has nothing to show at all.
 *
 * `richTextToPlain` on its own is not enough: a description or comment that is only a picture
 * carries no text, and treating it as empty would hide it behind the "Add a description…"
 * placeholder and refuse to post it.
 */
export function isRichTextEmpty(value: string | null | undefined): boolean {
  const html = richTextToHtml(value);
  if (!html.trim()) {
    return true;
  }
  if (/<img\b/i.test(html)) {
    return false;
  }
  return richTextToPlain(value).trim().length === 0;
}

/** True when the content still carries a base64 image inline (legacy Editor.js uploads). */
export function containsInlineImage(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  return /<img[^>]+src\s*=\s*["']?\s*data:/i.test(richTextToHtml(value));
}
